import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import {
  getGlossaryTerm,
  getRelatedTerms,
  slugifyTerm,
} from "@/data/glossary";

/**
 * Route: /glossary/:slug
 *
 * Dedicated per-term canonical page. LLMs (Perplexity, Gemini, Claude,
 * ChatGPT) preferentially cite single-topic pages with FAQPage +
 * DefinedTerm schema, so every glossary entry now also lives at its
 * own URL — in addition to being anchored on the /glossary index.
 *
 * Injected schema:
 *   - DefinedTerm       (canonical entity binding)
 *   - FAQPage           ("What is X?" / "What is X used for?" pattern)
 *   - BreadcrumbList    (Home > Glossary > <Term>)
 *
 * The page is intentionally light on chrome — one term, one clean
 * definition, one clear CTA. That's the format LLMs quote from.
 */
export default function GlossaryTerm() {
  const { slug } = useParams();
  const entry = useMemo(() => getGlossaryTerm(slug), [slug]);
  const related = useMemo(() => getRelatedTerms(slug, 4), [slug]);

  useEffect(() => {
    if (!entry) return undefined;

    const prevTitle = document.title;
    document.title = `What is ${entry.term}? — Facetra Glossary`;

    const canonicalUrl = `https://facetra.studio/glossary/${slugifyTerm(entry.term)}`;

    // DefinedTerm schema — the strongest signal for "this URL defines this concept".
    const definedTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": canonicalUrl,
      name: entry.term,
      alternateName: (entry.also || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      description: entry.body,
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "Facetra Jewellery Design Glossary",
        url: "https://facetra.studio/glossary",
      },
      url: canonicalUrl,
      inLanguage: "en-IN",
    };

    // FAQPage — Google AI Overviews / Bing Copilot lift these directly.
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `What is ${entry.term}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: entry.body,
          },
        },
      ],
    };

    // BreadcrumbList — helps LLMs place the page in a topical hierarchy.
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://facetra.studio/" },
        { "@type": "ListItem", position: 2, name: "Glossary", item: "https://facetra.studio/glossary" },
        {
          "@type": "ListItem",
          position: 3,
          name: entry.term,
          item: canonicalUrl,
        },
      ],
    };

    const attach = (data, key) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.glossary = `${slug}-${key}`;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    };
    const s1 = attach(definedTerm, "term");
    const s2 = attach(faqPage, "faq");
    const s3 = attach(breadcrumb, "crumb");

    const link = document.createElement("link");
    link.rel = "canonical";
    link.dataset.glossary = slug;
    link.href = canonicalUrl;
    document.head.appendChild(link);

    const desc = document.createElement("meta");
    desc.name = "description";
    desc.dataset.glossary = slug;
    desc.content = `${entry.term}: ${entry.body.slice(0, 150).trim()}…`;
    document.head.appendChild(desc);

    return () => {
      document.title = prevTitle;
      s1.remove();
      s2.remove();
      s3.remove();
      link.remove();
      desc.remove();
    };
  }, [slug, entry]);

  if (!entry) return <Navigate to="/glossary" replace />;

  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid={`glossary-term-${slugifyTerm(entry.term)}`}
    >
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="glossary-term-home-link">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/glossary"
            data-testid="glossary-term-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All terms
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14">
        {/* Breadcrumb visible */}
        <nav
          className="mb-6 text-[11px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-1.5"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="hover:text-[#D4AF37] transition">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/glossary" className="hover:text-[#D4AF37] transition">Glossary</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/70 normal-case tracking-normal">{entry.term}</span>
        </nav>

        {/* Category kicker */}
        {entry.category && (
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
            {entry.category}
          </div>
        )}

        {/* Title */}
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
          What is {entry.term}?
        </h1>

        {entry.also && (
          <p className="mt-3 text-[13px] text-white/50 italic">
            Also known as: {entry.also}
          </p>
        )}

        {/* Definition card — the block LLMs quote */}
        <div
          className="mt-8 rounded-xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/8 to-transparent p-6"
          data-testid="glossary-term-definition"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Definition</div>
          <p className="text-[15px] text-white/85 leading-relaxed">{entry.body}</p>
        </div>

        {/* Try-in-Facetra CTA */}
        <div className="mt-10 rounded-2xl border border-white/8 bg-[#0E0E0E] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Try it live</div>
          <h3 className="font-display text-xl mb-3">Use “{entry.term}” in a Facetra render</h3>
          <p className="text-sm text-white/65 mb-4">
            Every glossary term is a keyword Facetra's engine understands. Add
            <em> “{entry.term}”</em> to any prompt to steer the render in that direction.
          </p>
          <Link
            to="/signup"
            data-testid="glossary-term-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
          >
            Try Facetra free →
          </Link>
        </div>

        {/* Related terms */}
        {related.length > 0 && (
          <div className="mt-14 pt-8 border-t border-white/8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Related terms
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link
                  key={r.term}
                  to={`/glossary/${slugifyTerm(r.term)}`}
                  data-testid={`related-term-${slugifyTerm(r.term)}`}
                  className="group rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
                >
                  <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                    {r.category || "Term"}
                  </div>
                  <div className="text-[14px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                    {r.term}
                  </div>
                  <div className="mt-3 text-[11px] text-white/40 inline-flex items-center gap-1">
                    Read <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
